/* eslint-disable jsx-a11y/click-events-have-key-events */
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { addDays, addYears, getMonth, getYear, subDays, addMonths, subMonths } from 'date-fns';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import fr from 'date-fns/locale/fr/index.js';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import { useLanguageParam } from '~/config/i18n';

const DatePicker = (ReactDatePicker as unknown as { default: typeof ReactDatePicker }).default ?? ReactDatePicker;

registerLocale('fr', fr);

function datesBetweenIncluding(left: Date, right: Date) {
  // console.log('datesBetweenIncluding', left, right);
  const dates = [];
  let currentDate: Date = left;
  currentDate.setHours(0, 0, 0, 0);
  while (currentDate <= right) {
    dates.push(currentDate);
    currentDate = addDays(currentDate, 1);
  }
  return dates;
}

const DatesRecap = ({
  startDate,
  endDate,
  userDidInteract,
  onStartClicked,
  onEndClicked,
}: {
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  userDidInteract: boolean;
  onStartClicked?: () => void;
  onEndClicked?: () => void;
}) => {
  const [startActive, setStartActive] = useState(true);
  const { t } = useTranslation();
  const lang = useLanguageParam();
  const startAndEndSet = startDate && endDate;

  useEffect(() => {
    if (!userDidInteract || (!startDate && !endDate)) {
      setStartActive(true);
    } else {
      setStartActive(false);
    }
  }, [startDate, endDate, userDidInteract]);

  return (
    <>
      <div className="d-block d-md-none">
        <div className="d-flex flex-row">
          <div
            className={`day-recap ${startAndEndSet ? 'cursor-pointer' : ''} ${startActive ? 'active' : ''}`}
            onClick={() => {
              if (startAndEndSet) setStartActive(true);
              if (onStartClicked) onStartClicked();
            }}
          >
            {startDate ? (
              <span className="d-flex flex-column recap-underline mb-4">
                <div className="mb-2 recap-top">{t('office.startDate.label')}</div>
                <div className="recap-bottom">
                  {DateTime.fromJSDate(startDate).setLocale(lang).toLocaleString(DateTime.DATE_SHORT)}
                </div>
              </span>
            ) : (
              <span className="d-flex flex-column recap-underline -not-set mb-4">
                <div className="mb-2 recap-top">{t('global.btns.select')}</div>
                <div className="recap-bottom">{t('office.startDate.label')}</div>
              </span>
            )}
          </div>
          <div
            className={`day-recap ${startAndEndSet ? 'cursor-pointer' : ''} ${!startActive ? 'active' : ''}`}
            onClick={() => {
              if (startAndEndSet) setStartActive(false);
              if (onEndClicked) onEndClicked();
            }}
          >
            {endDate ? (
              <span className="d-flex flex-column recap-underline mb-4">
                <div className="mb-2 recap-top">{t('office.endDate.label')}</div>
                <div className="recap-bottom">
                  {DateTime.fromJSDate(endDate).setLocale(lang).toLocaleString(DateTime.DATE_SHORT)}
                </div>
              </span>
            ) : (
              <span className="d-flex flex-column recap-underline -not-set mb-4">
                <div className="mb-2 recap-top">{t('global.btns.select')}</div>
                <div className="recap-bottom">{t('office.endDate.label')}</div>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="d-none d-md-block mt-4 --desktop">
        <div className="d-flex flex-row mb-4">
          <div
            className={`day-recap ${startAndEndSet ? 'cursor-pointer' : ''} -desktop day-start ${
              startActive ? 'active' : ''
            }`}
            onClick={() => {
              if (startAndEndSet) setStartActive(true);
              if (onStartClicked) onStartClicked();
            }}
          >
            {startDate ? (
              <span className="d-flex flex-row recap-underline">
                <div className="mb-2 recap-top">{t('office.startDate.label')}</div>
                <div className="recap-bottom">
                  {DateTime.fromJSDate(startDate).setLocale(lang).toLocaleString(DateTime.DATE_SHORT)}
                </div>
              </span>
            ) : (
              <span className="d-flex flex-row recap-underline -not-set">
                <div className="mb-2 recap-top">{t('global.btns.select')}</div>
                <div className="recap-bottom">{t('office.startDate.label')}</div>
              </span>
            )}
          </div>
          <div className="recap-separator"></div>
          <div
            className={`day-recap ${startAndEndSet ? 'cursor-pointer' : ''} -desktop day-end ${
              !startActive ? 'active' : ''
            }`}
            onClick={() => {
              if (startAndEndSet) setStartActive(false);
              if (onEndClicked) onEndClicked();
            }}
          >
            {endDate ? (
              <span className="d-flex flex-row recap-underline">
                <div className="mb-2 recap-top">{t('office.endDate.label')}</div>
                <div className="recap-bottom">
                  {DateTime.fromJSDate(endDate).setLocale(lang).toLocaleString(DateTime.DATE_SHORT)}
                </div>
              </span>
            ) : (
              <span className="d-flex flex-row recap-underline -not-set">
                <div className="mb-2 recap-top">{t('global.btns.select')}</div>
                <div className="recap-bottom">{t('office.endDate.label')}</div>
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const BottomBar = ({
  availableNow,
  startDate,
  endDate,
  minDuration,
  maxDuration,
  onClearClicked,
  onApplyClicked,
}: {
  availableNow: boolean;
  startDate: Date;
  endDate?: Date;
  minDuration: number;
  maxDuration: number;
  onClearClicked: () => void;
  onApplyClicked: () => void;
}) => {
  const { t } = useTranslation();

  function availableFrom() {
    return (
      <>
        {availableNow ? (
          endDate ? (
            <>
              {t('office.availabilityDatepicker.fromNowToEndPrefix')}{' '}
              <span className="-medium">{t('office.availabilityDatepicker.nowTagText')}</span>{' '}
              {t('office.availabilityDatepicker.to')}{' '}
              <span className="-medium">
                {DateTime.fromJSDate(endDate).toFormat(t('date.formats.onlyDateSlashSeparated'))}
              </span>
            </>
          ) : (
            <>
              {t('office.availabilityDatepicker.fromNowNoEnd')}
              <span className="-medium"> {t('office.availabilityDatepicker.nowTagText')}</span>
            </>
          )
        ) : endDate ? (
          <>
            {t('office.availabilityDatepicker.fromStartToEndPrefix')}{' '}
            <span className="-medium">
              {DateTime.fromJSDate(startDate).toFormat(t('date.formats.onlyDateSlashSeparated'))}
            </span>{' '}
            {t('office.availabilityDatepicker.to')}{' '}
            <span className="-medium">
              {DateTime.fromJSDate(endDate).toFormat(t('date.formats.onlyDateSlashSeparated'))}
            </span>
          </>
        ) : (
          <>
            {t('office.availabilityDatepicker.fromStartNoEnd')}{' '}
            <span className="-medium">
              {DateTime.fromJSDate(startDate).toFormat(t('date.formats.onlyDateSlashSeparated'))}
            </span>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <div className="d-flex flex-column flex-md-row justify-content-between fo-border-top-sm border-grey-300">
        <div className="d-md-none text-center px-4 py-1 py-md-4 fo-border-bottom-sm border-grey-300 bg-light-grey">
          <div>
            <div className="p-1">{availableFrom()}</div>
            <div className="p-1 color-grey-500">
              {t('office.show.availability.possibleDurationStart')}{' '}
              <span className="-medium">
                {minDuration == maxDuration
                  ? t('office.show.availability.possibleDurationSingleMonthEnd', {
                      number: minDuration,
                    })
                  : t('office.show.availability.possibleDurationEnd', {
                      minNumber: minDuration,
                      maxNumber: maxDuration,
                    })}
              </span>
            </div>
          </div>
        </div>
        <div className="d-none d-md-block p-4">
          <div className="d-flex flex-column h-100 justify-content-between">
            <div className="text-1 -regular">{availableFrom()}</div>
            <div className="text-2 -regular color-grey-500">
              {t('office.show.availability.possibleDurationStart')}{' '}
              <span className="-medium">
                {minDuration == maxDuration
                  ? t('office.show.availability.possibleDurationSingleMonthEnd', {
                      number: minDuration,
                    })
                  : t('office.show.availability.possibleDurationEnd', {
                      minNumber: minDuration,
                      maxNumber: maxDuration,
                    })}
              </span>
            </div>
          </div>
        </div>
        <div className="d-flex flex-row justify-content-between justify-content-md-end p-4 bg-white">
          <button
            type="button"
            onClick={onClearClicked}
            className="btn btn-fo-outline-primary btn-lg px-8"
          >
            {t('global.btns.clear')}
          </button>
          <button
            type="button"
            onClick={onApplyClicked}
            className="btn btn-fo-primary btn-lg ms-0 ms-md-4 px-8"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

type TProps = {
  earliestStart: Date;
  latestEndOffice?: Date;
  minDuration: number;
  maxDuration: number;
  monthsShown: number;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  clear: () => void;
  apply: (start: Date, end: Date) => void;
  dismiss?: () => void;
};

const RequestDatepickerComponent = ({
  earliestStart,
  latestEndOffice,
  minDuration,
  maxDuration,
  monthsShown,
  selectedStartDate,
  selectedEndDate,
  clear,
  apply,
  dismiss,
}: TProps) => {
  const latestEnd = latestEndOffice ? latestEndOffice : addYears(new Date(), 2);
  const [startDate, setStartDate] = useState<Date | undefined>(selectedStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(selectedEndDate);
  const [earliestPick, setEarliestPick] = useState(earliestStart);
  const [latestPick, setLatestPick] = useState(latestEnd);
  const [selectedDate, setSelectedDate] = useState<Date | null>();
  const [excludedDays, setExcludedDays] = useState<Date[]>([]);
  const [outlinedDays, setOutlinedDays] = useState<{ date: string; holidayName: string }[]>([]);
  const [userDidInteract, setUserDidInteract] = useState(false);

  const { t } = useTranslation();
  const lang = useLanguageParam();
  const availableNow = earliestStart <= new Date();
  const months = possibleMonths(lang, earliestStart, latestEnd);
  const monthsForSelectLeft = Array.from(months);
  monthsForSelectLeft.pop();
  const monthsForSelectRight = Array.from(months);
  monthsForSelectRight.shift();

  function possibleMonths(lang: string, earliestStart: Date, latestEnd: Date) {
    const latestEndPlusOneMonth = addMonths(latestEnd, 1);
    const months = [];
    let currentDate: Date = subMonths(earliestStart, 1);
    while (currentDate <= latestEndPlusOneMonth) {
      months.push({
        display: currentDate.toLocaleString(lang, {
          month: 'long',
          year: 'numeric',
        }),
        month: getMonth(currentDate),
        year: getYear(currentDate),
      });
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  function findInMonths(ms: typeof months, month: number, year: number) {
    return ms.find((m) => m.month === month && m.year === year) || ms[0];
  }

  const handleOnChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setUserDidInteract(true);
    setStartDate(start || undefined);
    if (!end) {
      setSelectedDate(start);
    }
    setEndDate(end || undefined);
  };

  useEffect(() => {
    if (startDate && !endDate) {
      // if (endDate === null) {
      const nextEarliestPick = subDays(addMonths(startDate, minDuration), 1);
      setEarliestPick(nextEarliestPick);
      setSelectedDate(nextEarliestPick);
      const latestByMaxDuration = subDays(addMonths(startDate, maxDuration), 1);
      const nextLatestPick = latestByMaxDuration > latestEnd ? latestEnd : latestByMaxDuration;
      setLatestPick(nextLatestPick);
    } else {
      setEarliestPick(earliestStart);
      setLatestPick(addDays(subMonths(latestEnd, minDuration), 1));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setExcludedDays(
      datesBetweenIncluding(earliestStart, subDays(earliestPick, 1)).concat(
        datesBetweenIncluding(addDays(latestPick, 1), latestEnd),
      ),
    );
    // if (endDate === null && startDate !== null && startDate !== undefined) {
    if (endDate == undefined && startDate) {
      const earliestPickString = earliestPick.toUTCString();
      const latestPickString = latestPick.toUTCString();
      // console.log('earliestPickString', earliestPickString);
      // console.log('latestPickString', latestPickString);
      // TODO: these can't match ATM because of timezone divergence
      if (earliestPickString === latestPickString) {
        setOutlinedDays([{ date: latestPick.toISOString(), holidayName: t('office.latestEndDate.badge') }]);
      } else {
        setOutlinedDays([
          { date: earliestPick.toISOString(), holidayName: t('office.earliestEndDate.badge') },
          { date: latestPick.toISOString(), holidayName: t('office.latestEndDate.badge') },
        ]);
      }
    } else {
      setOutlinedDays([
        { date: earliestPick.toISOString(), holidayName: t('office.earliestStartDate.badge') },
        { date: latestPick.toISOString(), holidayName: t('office.latestStartDate.badge') },
      ]);
    }
  }, [earliestPick, latestPick]);

  function resetDatePicker() {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedDate(earliestStart);
    clear();
  }

  function monthValue(monthDate: Date) {
    const monthFound = findInMonths(months, getMonth(monthDate), getYear(monthDate));
    return monthFound === undefined ? 'notFound' : monthFound.display;
  }

  useEffect(() => {
    const closeDropdown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dismiss) dismiss();
      }
    };
    window.addEventListener('keydown', closeDropdown);
    return () => window.removeEventListener('keydown', closeDropdown);
  }, [dismiss]);

  return (
    <div className="request-datepicker">
      <DatesRecap
        startDate={startDate}
        endDate={endDate}
        userDidInteract={userDidInteract}
        onStartClicked={() => {
          setUserDidInteract(true);
          if (startDate) setSelectedDate(startDate);
        }}
        onEndClicked={() => {
          setUserDidInteract(true);
          if (endDate) setSelectedDate(endDate);
        }}
      />

      <div className="z-2 mx-auto mb-4 flex-grow-1 react-datepicker-wrapper">
        <DatePicker
          locale={lang}
          selected={selectedDate}
          onChange={handleOnChange}
          selectsRange
          startDate={startDate || undefined}
          endDate={endDate || undefined}
          inline
          holidays={outlinedDays}
          disabledKeyboardNavigation
          minDate={earliestStart}
          maxDate={latestEnd}
          excludeDates={excludedDays}
          showDisabledMonthNavigation
          calendarStartDay={1}
          useWeekdaysShort={true}
          selectsDisabledDaysInRange
          renderCustomHeader={({
            monthDate,
            changeMonth,
            changeYear,
            customHeaderCount,
            decreaseMonth,
            increaseMonth,
          }: {
            date: Date;
            monthDate: Date;
            changeMonth: (arg0: number) => void;
            changeYear: (arg0: number) => void;
            customHeaderCount: number;
            decreaseMonth: () => void;
            increaseMonth: () => void;
            prevMonthButtonDisabled: boolean;
            nextMonthButtonDisabled: boolean;
          }) => (
            <div className="d-flex justify-content-between mb-4">
              <div style={{ height: 24 }}>
                {(monthsShown == 1 || customHeaderCount == 0) &&
                  months.map((e) => e.display).indexOf(monthValue(monthDate)) > 0 && (
                    <button
                      className="h-100"
                      type="button"
                      onClick={decreaseMonth}
                    >
                      <SVGIcon iconType="chevron-left" />
                    </button>
                  )}
              </div>

              <select
                className="fo-default-select cursor-pointer"
                value={monthValue(monthDate)}
                onChange={({ target: { value } }) => {
                  let index = months.map((e) => e.display).indexOf(value);
                  if (index == 0 && customHeaderCount == 1) index = 1;
                  const m = months[index - customHeaderCount];
                  changeMonth(m.month);
                  changeYear(m.year);
                }}
              >
                {monthsShown == 1 &&
                  months.map((option) => (
                    <option
                      className="fo-default-select__option"
                      key={option.display}
                      value={option.display}
                    >
                      {option.display}
                    </option>
                  ))}
                {monthsShown != 1 &&
                  customHeaderCount == 0 &&
                  monthsForSelectLeft.map((option) => (
                    <option
                      className="fo-default-select__option"
                      key={option.display}
                      value={option.display}
                    >
                      {option.display}
                    </option>
                  ))}
                {customHeaderCount == 1 &&
                  monthsForSelectRight.map((option) => (
                    <option
                      className="fo-default-select__option"
                      key={option.display}
                      value={option.display}
                    >
                      {option.display}
                    </option>
                  ))}
              </select>
              <div style={{ height: 24 }}>
                {monthsShown == 1 &&
                  months.map((e) => e.display).indexOf(monthValue(monthDate)) < months.length - 1 && (
                    <button
                      type="button"
                      onClick={increaseMonth}
                    >
                      <SVGIcon iconType="chevron-right" />
                    </button>
                  )}
                {monthsShown == 2 &&
                  customHeaderCount == 1 &&
                  months.map((e) => e.display).indexOf(monthValue(monthDate)) < months.length - 1 && (
                    <button
                      type="button"
                      onClick={increaseMonth}
                    >
                      <SVGIcon iconType="chevron-right" />
                    </button>
                  )}
              </div>
            </div>
          )}
          monthsShown={monthsShown}
        />
        {/*{duration >= 0 && <div className="mt-4">{t('office.durationWithDays', { duration: duration })}</div>}*/}
      </div>
      <BottomBar
        availableNow={availableNow}
        startDate={earliestStart}
        endDate={latestEndOffice}
        minDuration={minDuration}
        maxDuration={maxDuration}
        onClearClicked={() => {
          setUserDidInteract(false);
          resetDatePicker();
        }}
        onApplyClicked={() => {
          setUserDidInteract(false);
          if (startDate && endDate) apply(startDate, endDate);
        }}
      />
    </div>
  );
};

export default RequestDatepickerComponent;
